import { useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, api } from "@/lib/api";
import UploadPhase from "./receipt-scanner/UploadPhase";
import AnalyzingPhase from "./receipt-scanner/AnalyzingPhase";
import ReviewPhase from "./receipt-scanner/ReviewPhase";
import LearnExpiryDialog from "./LearnExpiryDialog";
import { usePerm } from "@/context/AuthContext";

// Stop-words italiane + descrittori comuni di formato/packaging (NON discriminanti per il match).
// IMPORTANTE: "fresco/fresca" NON è qui dentro perché distingue "Salsiccia Fresca" da "Salsiccia Stagionata".
const _MATCH_STOPWORDS = new Set([
  "di","del","della","dello","dei","degli","delle","da","dal","dalla","in","con","al","alla","allo","lo","la","il","gli","le","un","una","uno","e","ed","a",
  "sciolto","sciolta","sottovuoto","affettato","affettata","grattugiato","grattugiata","busta","vaschetta","kg","g","gr","ml","cl","l","lt","hg",
]);

function _tokenize(name) {
  if (!name) return [];
  return String(name)
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .split(/\s+/)
    .filter((t) => t && t.length >= 2 && !_MATCH_STOPWORDS.has(t) && !/^\d/.test(t));
}

/**
 * Estrae la grammatura in grammi da una stringa (es. "80 g", "400G", "1kg", "0,5 kg" → 80 / 400 / 1000 / 500).
 * Ritorna null se non trovata. Usa la PRIMA occorrenza — le grammature multiple sono rare in anagrafica.
 */
function _extractGrams(s) {
  if (!s) return null;
  const m = String(s).toLowerCase().replace(",", ".").match(/(\d+(?:\.\d+)?)\s*(kg|g|gr)\b/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  const u = m[2];
  if (!Number.isFinite(n)) return null;
  return u === "kg" ? Math.round(n * 1000) : Math.round(n);
}

/**
 * Match per SCAN VENDITA: nomi da bilancia spesso divergono dall'anagrafica.
 * Preselezione RIGIDA: score finale ≥ 0.95, con peso forte a lotto e penalità
 * su grammatura differente. NON esce mai un match approssimativo silenzioso.
 *
 * Safety extra: se due o più prodotti pareggiano al top score, ritorna null.
 * Esempio critico che questa funzione previene:
 *   scan "Salsiccia Fresca 80 g" con in anagrafica "Salsiccia Fresca 80 g" +
 *   "Salsiccia Fresca 400 g" → NON auto-seleziona la 400 g (penalità -0.5).
 *
 * Il flusso acquisti/DDT NON usa questa funzione (usa MatchSuggestions su
 * POST /api/products/match dal backend).
 */
function findBestSaleMatch(scannedName, scannedBatch, products) {
  const aRaw = _tokenize(scannedName);
  if (aRaw.length === 0) return null;
  const aSet = new Set(aRaw);
  const aGrams = _extractGrams(scannedName);
  const aBatch = (scannedBatch || "").trim().toLowerCase();

  let best = null;
  let bestScore = -Infinity;
  let tieCount = 0;
  for (const p of products) {
    const bRaw = _tokenize(p.name);
    if (bRaw.length === 0) continue;
    const bSet = new Set(bRaw);
    let inter = 0;
    for (const t of aSet) if (bSet.has(t)) inter += 1;
    const union = aSet.size + bSet.size - inter;
    let score = union > 0 ? inter / union : 0;

    // Penalità forte se le grammature esistono su entrambi ma sono diverse
    // (es. "80 G" vs "400 G" → -0.5). Se una manca, nessuna penalità.
    const bGrams = _extractGrams(p.name) ?? _extractGrams(p.weight);
    if (aGrams != null && bGrams != null && aGrams !== bGrams) {
      score -= 0.5;
    }

    // Boost forte se stesso lotto: sullo scontrino vendita, coincidenza di lotto
    // fra scan e anagrafica pesa più della sola similarità di nome.
    const pBatch = (p.batch || "").trim().toLowerCase();
    if (aBatch && pBatch && aBatch === pBatch) {
      score += 0.25;
    }

    if (score > bestScore) {
      bestScore = score;
      best = p;
      tieCount = 1;
    } else if (score === bestScore) {
      tieCount += 1;
    }
  }
  // Soglia rigida: solo match ad alta confidenza vanno preselezionati.
  // Se c'è ambiguità (più prodotti pareggiano al top), non auto-selezionare.
  if (tieCount > 1) return null;
  return bestScore >= 0.95 ? best : null;
}

export default function ReceiptScanner({ open, onOpenChange, scanType = "product", products = [], onSaved }) {
  // Guest in sola lettura: lo scanner funziona (OCR), ma il salvataggio è bloccato.
  // Nota: due hook separati (non in corto-circuito) per rispettare le Rules of Hooks di React.
  const canWriteProducts = usePerm("products_write");
  const canWriteSales = usePerm("sales_write");
  const canPersist = canWriteProducts || canWriteSales;
  // photos: [{ id, file, preview, status: 'pending'|'analyzing'|'done'|'error', meta?, error? }]
  const [photos, setPhotos] = useState([]);
  const [phase, setPhase] = useState("upload"); // upload | analyzing | review
  const [docType, setDocType] = useState("receipt");
  const [rows, setRows] = useState([]); // rows tagged with __photoIndex
  const [saving, setSaving] = useState(false);
  const [learnDialogOpen, setLearnDialogOpen] = useState(false);
  const [learnCandidates, setLearnCandidates] = useState([]);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const isProduct = scanType === "product";
  const selectedCount = rows.filter((r) => r.__selected).length;

  // Livello 1 — Prevenzione errore "prodotto non associato" in modalità vendita:
  // conta le righe selezionate, non ancora salvate, senza product_id valorizzato
  // (né riga associata al magazzino, né marcata "nuovo prodotto" via "__create_new__").
  // Il pulsante di salvataggio è disabilitato finché questo contatore è > 0.
  const unassignedCount = isProduct
    ? 0
    : rows.filter((r) => r.__selected && !r.__saved && !r.product_id).length;
  const savedSelectedCount = rows.filter((r) => r.__selected && r.__saved).length;
  const pendingSelectedCount = selectedCount - savedSelectedCount;

  const reset = () => {
    photos.forEach((p) => {
      try { URL.revokeObjectURL(p.preview); } catch (_e) { /* noop */ }
    });
    setPhotos([]);
    setRows([]);
    setPhase("upload");
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const addFiles = (filesList) => {
    const valid = [];
    Array.from(filesList || []).forEach((f) => {
      if (!["image/jpeg", "image/png", "image/webp", "image/jpg"].includes(f.type)) {
        toast.error(`${f.name}: formato non supportato`, { description: "Usa JPEG, PNG o WEBP" });
        return;
      }
      valid.push({
        id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        preview: URL.createObjectURL(f),
        status: "pending",
      });
    });
    if (valid.length) {
      setPhotos((prev) => [...prev, ...valid]);
    }
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const removePhoto = (id) => {
    setPhotos((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed) {
        try { URL.revokeObjectURL(removed.preview); } catch (_e) { /* noop */ }
      }
      return prev.filter((p) => p.id !== id);
    });
  };

  const analyze = async () => {
    if (!photos.length) return;
    setPhase("analyzing");
    setPhotos((prev) => prev.map((p) => ({ ...p, status: "analyzing", error: undefined })));

    const scanOne = async (photo) => {
      const fd = new FormData();
      fd.append("file", photo.file);
      fd.append("scan_type", scanType);
      fd.append("document_type", docType);
      const res = await axios.post(`${API}/receipt/scan`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      return res.data;
    };

    const results = await Promise.allSettled(photos.map(scanOne));

    const allRows = [];
    const updatedPhotos = photos.map((p, idx) => {
      const r = results[idx];
      if (r.status === "fulfilled") {
        const data = r.value;
        const supplierDefaultDiscount = Number(data.supplier_default_discount_pct) || 0;
        (data.items || []).forEach((it, ridx) => {
          // Auto-fill price_per_kg for weight sales when AI provided only total + weight
          let computed = { ...it };

          // === SCONTO INTELLIGENTE per scan prodotti (3 strategie) ===
          if (scanType === "product") {
            const aiDiscount = Number(it.discount_pct) || 0;
            if (!aiDiscount) {
              // 1. Calcolo da netto vs lordo
              const m = String(it.weight || "").trim().toLowerCase().replace(",", ".").match(/^\s*([0-9]*\.?[0-9]+)\s*([a-z]*)/);
              let kg = 0;
              if (m) {
                const num = parseFloat(m[1]);
                const unit = (m[2] || "kg").trim();
                kg = unit === "g" || unit === "gr" ? num / 1000 : unit === "hg" ? num / 10 : num;
              }
              const isWeightItem = !!it.is_weight_unit || (!!it.price_per_kg && kg > 0);
              const grossUnit = isWeightItem ? Number(it.price_per_kg) || Number(it.unit_price) || 0 : Number(it.unit_price) || 0;
              const grossQty = isWeightItem ? kg : (Number(it.quantity) || 1);
              const netTotal = Number(it.total_price) || 0;
              if (grossUnit > 0 && grossQty > 0 && netTotal > 0) {
                const grossTotal = grossUnit * grossQty;
                if (grossTotal > netTotal + 0.01) {
                  const calc = (1 - netTotal / grossTotal) * 100;
                  if (calc >= 1 && calc <= 90) {
                    computed.discount_pct = Math.round(calc * 10) / 10;
                    computed.__discount_source = "calculated";
                  }
                }
              }
              // 2. Fallback memoria fornitore
              if (!computed.discount_pct && supplierDefaultDiscount > 0) {
                computed.discount_pct = supplierDefaultDiscount;
                computed.__discount_source = "supplier_memory";
              }
            } else {
              computed.__discount_source = "ai";
            }
          }

          if (scanType === "sale" && (it.sale_mode === "weight" || (!!it.weight && !it.sale_mode))) {
            const m = String(it.weight || "").trim().toLowerCase().replace(",", ".").match(/^\s*([0-9]*\.?[0-9]+)\s*([a-z]*)/);
            let kg = 0;
            if (m) {
              const num = parseFloat(m[1]);
              const unit = (m[2] || "kg").trim();
              kg = unit === "g" || unit === "gr" ? num / 1000 : unit === "hg" ? num / 10 : num;
            }
            const totalGuess = Number(it.total_price) || Number(it.unit_price) || 0;
            if (!it.price_per_kg && kg > 0 && totalGuess > 0) {
              computed.price_per_kg = Math.round((totalGuess / kg) * 100) / 100;
            }
            computed.sale_mode = "weight";
          }
          allRows.push({
            ...computed,
            // Sui documenti gestiti dallo scanner (DDT, fatture, scontrini) il PLU
            // non compare mai: il codice estratto dall'OCR è sempre un codice
            // fornitore/articolo. Precompiliamo solo supplier_code, plu resta vuoto
            // (l'utente lo inserirà a mano in Impostazioni prodotto se serve).
            plu: null,
            supplier_code: computed.code || null,
            __id: `p${idx}-${ridx}-${Math.random().toString(36).slice(2, 6)}`,
            __photoIndex: idx,
            __selected: true,
            // === SCADENZA SUGGERITA AI ===
            // Se l'AI/documento ha già la data → tieni quella, source = document.
            // Altrimenti, se auto_apply abilitato → applica suggested_expiry_date e taggalo.
            expiry_date: (() => {
              if (computed.expiry_date) return computed.expiry_date;
              if (data.auto_apply_suggested_expiry && computed.suggested_expiry_date) {
                return computed.suggested_expiry_date;
              }
              return null;
            })(),
            __expiry_source: computed.expiry_date
              ? "document"
              : (data.auto_apply_suggested_expiry && computed.suggested_expiry_date
                ? (computed.suggested_source === "learned"
                  ? "learned"
                  : (computed.suggested_source === "ai" ? "ai_suggested" : "category_default"))
                : "missing"),
            product_id: scanType === "sale"
              ? (findBestSaleMatch(computed.product_name, computed.batch, products)?.id || "")
              : undefined,
            __auto_matched: scanType === "sale"
              ? !!findBestSaleMatch(computed.product_name, computed.batch, products)
              : undefined,
            // Stock iniziale: se l'AI dice "pz" usa quantity_pcs, altrimenti tieni la stima esistente.
            stock: scanType === "product"
              ? (computed.unit_of_measure === "pz"
                ? (Number(computed.quantity_pcs) || Number(computed.quantity) || 1)
                : (computed.quantity || 1))
              : undefined,
            // Pre-fissa il prezzo unitario per i pezzi (se l'AI l'ha fornito)
            price: scanType === "product" && computed.unit_of_measure === "pz" && (computed.price_per_piece || computed.unit_price)
              ? Number(computed.price_per_piece || computed.unit_price)
              : computed.price,
          });
        });
        return {
          ...p,
          status: "done",
          meta: {
            supplier: data.supplier,
            supplier_id: data.supplier_id,
            supplier_known: data.supplier_known,
            // Nuovi campi restituiti da POST /api/receipt/scan (Giro fornitori):
            //  - supplier_ambiguous / _candidates: >1 schede canonicamente uguali
            //  - supplier_similar_* : 0 canoniche + ≥1 quasi-match sopra soglia
            //  - supplier_ocr_name: nome grezzo letto dall'OCR (per la domanda UI)
            supplier_ambiguous: !!data.supplier_ambiguous,
            supplier_ambiguous_candidates: data.supplier_ambiguous_candidates || [],
            supplier_similar_proposal: !!data.supplier_similar_proposal,
            supplier_similar_candidates: data.supplier_similar_candidates || [],
            supplier_ocr_name: data.supplier_ocr_name || data.supplier || "",
            sold_at: data.sold_at,
            document_number: data.document_number,
            document_date: data.document_date,
            confidence: data.confidence || "media",
            items_count: (data.items || []).length,
          },
        };
      }
      return {
        ...p,
        status: "error",
        error: r.reason?.response?.data?.detail || r.reason?.message || "Errore",
      };
    });

    setPhotos(updatedPhotos);
    setRows(allRows);

    const okCount = updatedPhotos.filter((p) => p.status === "done").length;
    const errCount = updatedPhotos.length - okCount;
    if (okCount === 0) {
      toast.error("Nessuna foto leggibile", { description: errCount > 0 ? `${errCount} errori` : undefined });
      setPhase("upload");
      return;
    }
    if (errCount > 0) {
      toast.warning(`${okCount}/${updatedPhotos.length} foto analizzate`, {
        description: `${errCount} ${errCount === 1 ? "non leggibile" : "non leggibili"}`,
      });
    } else {
      toast.success(`${okCount} ${okCount === 1 ? "foto analizzata" : "foto analizzate"}`, {
        description: `${allRows.length} voci totali`,
      });
    }
    setPhase("review");
  };

  const updateRow = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.__id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.__id !== id));

  const applyGlobalDiscount = (pct) => {
    setRows((rs) => rs.map((r) => ({ ...r, discount_pct: pct, __discount_source: "global" })));
    if (pct > 0) {
      toast.success(`Sconto ${pct}% applicato a tutte le righe`);
    } else {
      toast.success("Sconto rimosso da tutte le righe");
    }
  };

  const applySuggestedToAll = () => {
    let count = 0;
    setRows((rs) => rs.map((r) => {
      if (!r.expiry_date && r.suggested_expiry_date) {
        count += 1;
        const src = r.suggested_source === "learned"
          ? "learned"
          : (r.suggested_source === "ai" ? "ai_suggested" : "category_default");
        return { ...r, expiry_date: r.suggested_expiry_date, __expiry_source: src };
      }
      return r;
    }));
    if (count > 0) {
      toast.success(`Scadenza suggerita applicata a ${count} ${count === 1 ? "riga" : "righe"}`);
    } else {
      toast.info("Tutte le righe hanno già una scadenza");
    }
  };

  // Risolve una domanda fornitore pendente sulla foto (quasi-match o ambiguo).
  // decision: "attach" (l'operatore ha detto "sì, è X" → supplierId di X) oppure
  //           "create" (l'operatore ha detto "no, è nuovo").
  // Passa al backend gli items realmente letti dal DDT per quella foto: così la
  // scheda scelta impara gli articoli anche se la prima chiamata /receipt/scan
  // aveva saltato la scrittura.
  const resolveSupplier = async (photoIndex, decision, supplierId = null) => {
    const photo = photos[photoIndex];
    if (!photo) return;
    const meta = photo.meta || {};
    const ocrName = meta.supplier_ocr_name || meta.supplier || "";
    if (!ocrName) {
      toast.error("Nome fornitore mancante");
      return;
    }
    const rowsForPhoto = rows.filter((r) => r.__photoIndex === photoIndex);
    const items = rowsForPhoto.map((r) => ({
      code: r.supplier_code || null,
      name: r.name || "",
      unit_price: Number(r.price) || null,
      category: r.category || null,
      weight: r.weight || null,
      is_fresh: !!r.is_fresh,
    }));
    const body = { decision, ocr_name: ocrName, items, doc_type: "ddt_invoice" };
    if (decision === "attach") {
      if (!supplierId) {
        toast.error("Fornitore non specificato");
        return;
      }
      body.supplier_id = supplierId;
    }
    try {
      const r = await axios.post(`${API}/receipt/scan/resolve-supplier`, body);
      const data = r.data || {};
      const sup = data.supplier || {};
      setPhotos((prev) =>
        prev.map((pp, i) =>
          i === photoIndex
            ? {
                ...pp,
                meta: {
                  ...pp.meta,
                  supplier: sup.name || pp.meta.supplier,
                  supplier_id: sup.id || null,
                  supplier_known: decision === "attach",
                  supplier_ambiguous: false,
                  supplier_ambiguous_candidates: [],
                  supplier_similar_proposal: false,
                  supplier_similar_candidates: [],
                },
              }
            : pp,
        ),
      );
      toast.success(
        decision === "attach"
          ? `Agganciato a "${sup.name || ocrName}"`
          : `Creata nuova scheda "${sup.name || ocrName}"`,
      );
    } catch (e) {
      toast.error("Impossibile aggiornare il fornitore", {
        description: e?.response?.data?.detail || e?.message || "Errore di rete",
      });
    }
  };

  // Domande pendenti sui fornitori: blocca il salvataggio finché l'operatore
  // non risponde. Un tocco basta a rispondere.
  const pendingSupplierQuestions = photos.filter(
    (p) => p.status === "done" && (p.meta?.supplier_similar_proposal || p.meta?.supplier_ambiguous),
  ).length;

  const save = async () => {
    const selected = rows.filter((r) => r.__selected);
    if (selected.length === 0) {
      toast.error("Seleziona almeno una voce");
      return;
    }

    // 1) Detect rows where the user manually overrode the AI/learned suggestion → ask to remember
    const candidates = [];
    if (scanType === "product") {
      for (const row of selected) {
        if (!row.expiry_date || !row.suggested_expiry_date || !row.ai_suggested_days) continue;
        // Compute the user's effective shelf life in days from today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const userDate = new Date(`${row.expiry_date}T00:00:00`);
        if (Number.isNaN(userDate.getTime())) continue;
        const diffMs = userDate.getTime() - today.getTime();
        const userDays = Math.round(diffMs / 86400000);
        if (userDays < 1 || userDays > 1095) continue;
        const aiDays = Math.round(Number(row.ai_suggested_days));
        if (!aiDays) continue;
        // Only ask to learn when the user diverged from the AI by ≥2 gg
        if (Math.abs(userDays - aiDays) >= 2 && row.__expiry_source !== "document") {
          candidates.push({
            row_id: row.__id,
            name: row.name,
            category: row.category,
            ai_days: aiDays,
            user_days: userDays,
            diff_days: userDays - aiDays,
          });
        }
      }
    }

    if (candidates.length > 0) {
      setLearnCandidates(candidates);
      setLearnDialogOpen(true);
      return; // wait for dialog handlers below
    }

    // No overrides to learn → save straight away
    await persistSelected(selected);
  };

  // Called when user clicks "Salva e ricorda" in LearnExpiryDialog
  const handleLearnConfirm = async (picks) => {
    if (picks && picks.length > 0) {
      try {
        const res = await api.confirmShelfLifeLearnings(
          picks.map((p) => ({
            name: p.name,
            category: p.category || null,
            ai_days: p.ai_days,
            user_days: p.user_days,
          }))
        );
        const ready = (res?.results || []).filter((r) => r.ready);
        if (ready.length > 0) {
          toast.success(`Imparate ${ready.length} preferenze`, {
            description: "Le prossime scansioni useranno automaticamente i tuoi valori.",
          });
        } else {
          toast.success("Preferenze registrate", {
            description: "Servono altre conferme per attivarle (min 3).",
          });
        }
      } catch (e) {
        toast.error("Errore salvataggio preferenze", { description: e?.response?.data?.detail || e?.message });
      }
    }
    setLearnDialogOpen(false);
    setLearnCandidates([]);
    const selected = rows.filter((r) => r.__selected);
    await persistSelected(selected);
  };

  // Called when user clicks "Solo questa volta" → just save, no learning
  const handleLearnSkip = async () => {
    setLearnDialogOpen(false);
    setLearnCandidates([]);
    const selected = rows.filter((r) => r.__selected);
    await persistSelected(selected);
  };

  const persistSelected = async (selected) => {
    setSaving(true);
    let ok = 0;
    const errors = [];
    // Livello 2c — Salta le righe già committate in un tentativo precedente
    // (marcate __saved). Evita doppie vendite/prodotti su retry dopo errore parziale.
    const toProcess = selected.filter((r) => !r.__saved);
    const savedRowIds = new Set();

    // Intra-scan dedup: mappa nome+categoria (normalizzati) → productId. Popolata
    // sia quando una riga si aggancia a un prodotto esistente (via __link_to_id),
    // sia dopo la creazione di un nuovo prodotto. Righe successive con la stessa
    // key riusano l'id via adjust-stock (nuovo lotto), evitando la card duplicata.
    // Bypass: __match_rejected → l'operatore ha esplicitamente chiesto "Crea nuovo".
    const productIdByKey = new Map();
    const _intraKey = (name, category) => {
      const n = (name || "").toLowerCase().normalize("NFKD").replace(/\s+/g, " ").trim();
      const c = (category || "").toLowerCase().trim();
      if (!n || !c) return null;
      return `${n}|${c}`;
    };

    for (const row of toProcess) {
      const photoMeta = photos[row.__photoIndex]?.meta;
      try {
        if (scanType === "product") {
          if (!row.name || !row.category) {
            errors.push(`${row.name || "(riga)"}: nome e categoria obbligatori`);
            continue;
          }
          // Modalità prodotto: priorità a unit_of_measure esplicito dall'AI (kg|pz),
          // poi fallback alla modalità manuale dell'utente, infine euristica price_per_kg.
          const explicitUom = (row.unit_of_measure || "").toLowerCase();
          const isWeightProduct =
            explicitUom === "kg"
              ? true
              : explicitUom === "pz"
              ? false
              : (row.is_weight_unit === true ||
                 row.product_mode === "weight" ||
                 (!row.product_mode && !!row.price_per_kg));

          // Sconto applicato (0-100) → fattore [0..1]
          const discountPct = Math.max(0, Math.min(100, Number(row.discount_pct) || 0));
          const discountFactor = 1 - discountPct / 100;

          // Prezzo LORDO letto dallo scontrino: fallback price_per_kg ↔ unit_price (a seconda della modalità)
          const grossPerKg = isWeightProduct
            ? Number(row.price_per_kg) || Number(row.unit_price) || 0
            : 0;
          const grossPerPiece = !isWeightProduct
            ? Number(row.unit_price) || Number(row.price_per_kg) || 0
            : 0;

          // Prezzo NETTO (post-sconto) = quello che salviamo come costo nel magazzino.
          // Per prodotti a peso solo cost_per_kg; per pezzo solo cost_price. Mai entrambi.
          const costPerKg = isWeightProduct ? +(grossPerKg * discountFactor).toFixed(4) : null;
          const costPrice = isWeightProduct
            ? null
            : +(grossPerPiece * discountFactor).toFixed(4);
          const stockVal = isWeightProduct ? 1 : Number(row.stock) || 1;

          // Per prodotti a peso: salva i kg nel campo dedicato stock_kg.
          // Il peso PESATO della riga va SOLO in stock_kg; il campo `weight`
          // del prodotto rappresenta la PEZZATURA della confezione (quanto
          // pesa un pezzo intero), che per lo scanner viene estratta lato
          // backend dal NOME quando il nome la contiene.
          let stockKg = null;
          if (isWeightProduct) {
            const m = String(row.weight || "").trim().toLowerCase().replace(",", ".").match(/^\s*([0-9]*\.?[0-9]+)\s*([a-z]*)/);
            if (m) {
              const num = parseFloat(m[1]);
              const unit = (m[2] || "kg").trim();
              stockKg = unit === "g" || unit === "gr" ? num / 1000 : unit === "hg" ? num / 10 : num;
              stockKg = Math.round(stockKg * 1000) / 1000;
            }
          }

          if (row.__link_to_id) {
            // Guardia Lotti: map delle sorgenti UI → semantica backend.
            //   document/manual/adopted → "read"  (autorevole, l'utente ha confermato)
            //   ai_suggested/category_default/learned/missing → "estimated"
            const readSources = new Set(["document", "manual", "adopted"]);
            const expirySource = readSources.has(row.__expiry_source) ? "read" : "estimated";
            await api.adjustStock(row.__link_to_id, {
              delta: isWeightProduct ? null : stockVal,
              delta_kg: isWeightProduct ? stockKg : null,
              batch: row.batch || null,
              expiry_date: row.expiry_date || null,
              cost_price: costPrice,
              cost_per_kg: costPerKg,
              expiry_source: expirySource,
              supplier_id: photoMeta?.supplier_id || null,
            });
            // Popola mappa intra-scan: righe successive con stesso nome+categoria
            // si aggancieranno a questo prodotto.
            const k = _intraKey(row.name, row.category);
            if (k) productIdByKey.set(k, row.__link_to_id);
          } else {
            // Intra-scan: prima di creare una card nuova, controlla se una riga
            // precedente dello stesso scan ha già creato/collegato lo stesso prodotto.
            // Bypass se l'operatore ha esplicitamente rifiutato il match.
            const key = _intraKey(row.name, row.category);
            const intraId = (!row.__match_rejected && key) ? productIdByKey.get(key) : null;
            if (intraId) {
              // Aggancio intra-scan: adjust-stock sul prodotto già creato dalla
              // riga precedente. La Guardia Lotti (lato backend) decide se
              // accumulare sul lotto o crearne uno nuovo in base a batch+expiry.
              const readSources = new Set(["document", "manual", "adopted"]);
              const expirySource = readSources.has(row.__expiry_source) ? "read" : "estimated";
              await api.adjustStock(intraId, {
                delta: isWeightProduct ? null : stockVal,
                delta_kg: isWeightProduct ? stockKg : null,
                batch: row.batch || null,
                expiry_date: row.expiry_date || null,
                cost_price: costPrice,
                cost_per_kg: costPerKg,
                expiry_source: expirySource,
                supplier_id: photoMeta?.supplier_id || null,
              });
            } else {
              const created = await axios.post(`${API}/products`, {
                name: row.name,
                category: row.category,
                // Prodotti a peso: NON mandiamo il peso pesato della riga nel
                // campo `weight` del prodotto. Il backend prova a estrarre la
                // pezzatura dal nome se c'è; altrimenti il campo resta vuoto
                // (è dell'operatore, non dello scanner).
                // Prodotti a pezzo: `row.weight` contiene la pezzatura letta
                // dal documento (DDT uom=pz) o null (scontrino uom=pz).
                weight: isWeightProduct ? null : (row.weight || null),
                batch: row.batch || "",
                expiry_date: row.expiry_date || null,
                sale_unit: isWeightProduct ? "weight" : "piece",
                stock: stockVal,
                stock_kg: stockKg,
                price: 0,
                price_per_kg: null,
                cost_price: costPrice,
                cost_per_kg: costPerKg,
                supplier: photoMeta?.supplier || "",
                supplier_id: photoMeta?.supplier_id || null,
                plu: row.plu || null,
                supplier_code: row.supplier_code || null,
                notes: row.notes || (discountPct > 0 ? `Sconto applicato: ${discountPct}% (lordo €${(isWeightProduct ? grossPerKg : grossPerPiece).toFixed(2)})` : ""),
                is_fresh: !!row.is_fresh,
              });
              // Popola mappa intra-scan con l'id del prodotto appena creato.
              const newId = created?.data?.id;
              if (key && newId) productIdByKey.set(key, newId);
            }
          }
          ok += 1;
          savedRowIds.add(row.__id);
        } else {
          if (!row.product_id) {
            errors.push(`${row.product_name || "(riga)"}: prodotto non associato`);
            continue;
          }
          const isWeightSale = row.sale_mode === "weight" || !!row.weight;
          const qty = Number(row.quantity) || 1;
          const ppk = isWeightSale ? Number(row.price_per_kg) || null : null;
          const unitPrice = isWeightSale
            ? ppk || Number(row.unit_price) || Number(row.total_price) || 0
            : Number(row.unit_price) || Number(row.total_price) || 0;

          // ── Modalità "Crea nuovo prodotto" durante una vendita ──
          // L'utente ha scelto "Nuovo prodotto (altro)" dal dropdown: creiamo prima il
          // prodotto in magazzino con i dati letti dallo scontrino, poi registriamo la vendita.
          let effectiveProductId = row.product_id;
          if (row.product_id === "__create_new__") {
            // Parsifica peso (es. "0.722 kg" / "200g") in kg
            let createStockKg = null;
            let weightStr = row.weight || "";
            if (isWeightSale && weightStr) {
              const m = String(weightStr).trim().toLowerCase().replace(",", ".").match(/^\s*([0-9]*\.?[0-9]+)\s*([a-z]*)/);
              if (m) {
                const num = parseFloat(m[1]);
                const unit = (m[2] || "kg").trim();
                createStockKg = unit === "g" || unit === "gr" ? num / 1000 : unit === "hg" ? num / 10 : num;
                createStockKg = Math.round(createStockKg * 1000) / 1000;
                weightStr = `${createStockKg.toFixed(3)} kg`;
              }
            }
            // Categoria di default: se l'utente non l'ha impostata, "Altro"
            const newName = (row.__new_product_name?.trim()) || row.product_name || "Prodotto da scontrino";
            const created = await axios.post(`${API}/products`, {
              name: newName,
              category: row.category || "Altro",
              weight: weightStr || "",
              batch: row.batch || "",
              expiry_date: null,
              stock: isWeightSale ? 1 : (qty || 0),
              stock_kg: isWeightSale ? createStockKg : null,
              price: isWeightSale ? (ppk || 0) : unitPrice,
              price_per_kg: isWeightSale ? (ppk || null) : null,
              cost_price: null,
              cost_per_kg: null,
              supplier: photoMeta?.supplier || "",
              plu: row.plu || null,
              supplier_code: row.supplier_code || null,
              notes: "Creato durante registrazione vendita",
              is_fresh: isWeightSale,
            });
            effectiveProductId = created.data?.id;
            if (!effectiveProductId) {
              errors.push(`${row.product_name}: creazione prodotto fallita`);
              continue;
            }
          }

          // Se il prodotto in magazzino non ha ancora un prezzo di listino (o è 0),
          // o se l'utente ha confermato l'aggiornamento → aggiorniamo il listino
          const linkedProduct = products.find((p) => p.id === effectiveProductId);
          if (linkedProduct) {
            const listingPpk = Number(linkedProduct.price_per_kg) || 0;
            const listingPrice = Number(linkedProduct.price) || 0;
            const isFirstPrice = isWeightSale ? (listingPpk === 0 && ppk > 0) : (listingPrice === 0 && unitPrice > 0);
            const isDifferent = isWeightSale
              ? (listingPpk > 0 && ppk > 0 && Math.abs(listingPpk - ppk) >= 0.01)
              : (listingPrice > 0 && unitPrice > 0 && Math.abs(listingPrice - unitPrice) >= 0.01);
            const userConfirmed = !!row.__update_listing_price;
            const shouldUpdate = isFirstPrice || (isDifferent && userConfirmed);
            if (shouldUpdate) {
              try {
                if (isWeightSale) {
                  await axios.put(`${API}/products/${effectiveProductId}`, { price_per_kg: ppk, price: ppk });
                } else {
                  await axios.put(`${API}/products/${effectiveProductId}`, { price: unitPrice });
                }
              } catch (_e) { /* non bloccante */ }
            }
          }

          await axios.post(`${API}/sales`, {
            product_id: effectiveProductId,
            quantity: isWeightSale ? 1 : qty,
            unit_price: unitPrice,
            price_per_kg: ppk,
            weight_sold: row.weight || null,
            batch: row.batch || null,
            sold_at: photoMeta?.sold_at || null,
          });
          ok += 1;
          savedRowIds.add(row.__id);
        }
      } catch (err) {
        errors.push(`${row.name || row.product_name}: ${err?.response?.data?.detail || err.message}`);
      }
    }
    setSaving(false);

    // Livello 2c: marca le righe committate come __saved. Le prossime chiamate
    // a persistSelected le salteranno (niente doppie vendite/prodotti).
    if (savedRowIds.size > 0) {
      setRows((rs) => rs.map((r) => (savedRowIds.has(r.__id) ? { ...r, __saved: true } : r)));
    }

    if (ok > 0) {
      // Memoria fornitore: aggiorna lo sconto medio del supplier dopo il save (solo per DDT prodotti)
      if (scanType === "product") {
        const supplierIdsAndDiscounts = new Map(); // supplier_id → [discounts]
        selected.forEach((row) => {
          const meta = photos[row.__photoIndex]?.meta;
          const sid = meta?.supplier_id;
          const d = Number(row.discount_pct) || 0;
          if (sid) {
            if (!supplierIdsAndDiscounts.has(sid)) supplierIdsAndDiscounts.set(sid, []);
            supplierIdsAndDiscounts.get(sid).push(d);
          }
        });
        for (const [sid, discounts] of supplierIdsAndDiscounts.entries()) {
          const avg = discounts.length ? discounts.reduce((a, b) => a + b, 0) / discounts.length : 0;
          if (avg > 0) {
            try {
              await axios.post(`${API}/suppliers/${sid}/learn-discount`, { discount_pct: avg });
            } catch (_e) { /* non bloccante */ }
          }
        }
      }
      toast.success(`${ok} ${scanType === "product" ? "prodotti aggiunti" : "vendite registrate"}`, {
        description: errors.length ? `${errors.length} righe con errori — correggi e riprova` : undefined,
      });
      if (errors.length) {
        errors.slice(0, 3).forEach((e) => toast.error(e));
      }
      // Refresh parent list in ogni caso (magazzino/vendite già aggiornati per le righe OK)
      onSaved?.();
      // Livello 2c: NON chiudere il modal se restano errori.
      // Chiudi solo se tutte le righe selezionate risultano ora __saved.
      const allSaved = selected.every((r) => r.__saved || savedRowIds.has(r.__id));
      if (!errors.length && allSaved) {
        handleClose(false);
      }
    } else {
      toast.error("Nessuna riga salvata", { description: errors[0] });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="receipt-scanner-dialog"
        className="
          fixed inset-0 translate-x-0 translate-y-0
          sm:left-[50%] sm:top-[50%] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:inset-auto
          w-full h-[100dvh] max-w-none rounded-none
          sm:w-[calc(100vw-1rem)] sm:h-auto sm:max-h-[92dvh] sm:max-w-3xl sm:rounded-lg
          p-0 gap-0 flex flex-col bg-white
        "
      >
        <DialogHeader className="px-4 sm:px-6 pt-4 pb-3 border-b border-[#EAE7E1] shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-[#E8F3EC] text-[#2E4A3B] flex items-center justify-center flex-shrink-0">
              <Sparkles size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle style={{ fontFamily: "Work Sans, sans-serif" }} className="text-left break-words">
                Scansiona scontrino
              </DialogTitle>
              <DialogDescription className="text-left break-words">
                {isProduct
                  ? "L'AI estrae ogni riga come prodotto separato — puoi rivedere e modificare prima di caricare."
                  : "L'AI estrae ogni riga come vendita separata — puoi rivedere e modificare prima di registrare."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-4">
          {phase === "upload" && (
            <UploadPhase
              scanType={scanType}
              docType={docType}
              setDocType={setDocType}
              photos={photos}
              addFiles={addFiles}
              removePhoto={removePhoto}
              onAnalyze={analyze}
              fileRef={fileRef}
              cameraRef={cameraRef}
            />
          )}

          {phase === "analyzing" && <AnalyzingPhase photos={photos} />}

          {phase === "review" && photos.some((p) => p.status === "done") && (
            <ReviewPhase
              photos={photos}
              rows={rows}
              isProduct={isProduct}
              products={products}
              selectedCount={selectedCount}
              saving={saving}
              onUpdateRow={updateRow}
              onRemoveRow={removeRow}
              onApplyGlobalDiscount={applyGlobalDiscount}
              onApplySuggestedExpiryToAll={applySuggestedToAll}
              onReset={reset}
              onCancel={() => handleClose(false)}
              onSave={save}
              onResolveSupplier={resolveSupplier}
            />
          )}
        </div>

        {phase === "review" && photos.some((p) => p.status === "done") && (
          <div
            data-testid="receipt-scanner-actions"
            className="shrink-0 border-t border-[#EAE7E1] bg-white px-4 sm:px-6 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pb-[max(env(safe-area-inset-bottom),0.75rem)]"
          >
            <Button
              variant="outline"
              onClick={() => handleClose(false)}
              className="border-[#EAE7E1] w-full sm:w-auto"
            >
              Annulla
            </Button>
            <div className="flex flex-col gap-1 items-end w-full sm:w-auto">
              {canPersist ? (
              <Button
                data-testid="receipt-save-btn"
                onClick={save}
                disabled={saving || pendingSelectedCount === 0 || unassignedCount > 0 || pendingSupplierQuestions > 0}
                className="bg-[#2E4A3B] hover:bg-[#21382C] text-white w-full sm:w-auto"
              >
                {saving
                  ? "Salvataggio..."
                  : isProduct
                  ? `Aggiungi ${pendingSelectedCount} al magazzino`
                  : `Registra ${pendingSelectedCount} vendit${pendingSelectedCount === 1 ? "a" : "e"}`}
              </Button>
              ) : (
                <div
                  data-testid="receipt-save-guest-note"
                  className="text-[11px] text-[#8E918C] text-right max-w-[260px]"
                >
                  Demo in sola lettura: lo scanner mostra il risultato dell&apos;AI, ma per salvare prodotti e fornitori devi accedere.
                </div>
              )}
              {pendingSupplierQuestions > 0 && (
                <p
                  data-testid="receipt-save-blocker-supplier"
                  className="text-[11px] text-[#8B6518] text-right"
                >
                  {pendingSupplierQuestions === 1
                    ? "1 fornitore da confermare"
                    : `${pendingSupplierQuestions} fornitori da confermare`}
                </p>
              )}
              {unassignedCount > 0 && (
                <p
                  data-testid="receipt-save-blocker"
                  className="text-[11px] text-[#8B6518] text-right"
                >
                  {unassignedCount === 1
                    ? "1 riga da associare"
                    : `${unassignedCount} righe da associare`}
                </p>
              )}
              {savedSelectedCount > 0 && pendingSelectedCount > 0 && (
                <p className="text-[11px] text-[#5C5F5A] text-right">
                  {savedSelectedCount} già {isProduct ? "aggiunt" : "registrat"}
                  {savedSelectedCount === 1 ? (isProduct ? "o" : "a") : (isProduct ? "i" : "e")}
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>

      <LearnExpiryDialog
        open={learnDialogOpen}
        onOpenChange={setLearnDialogOpen}
        candidates={learnCandidates}
        onConfirm={handleLearnConfirm}
        onSkip={handleLearnSkip}
      />
    </Dialog>
  );
}
